import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Filter } from 'lucide-react';
import { useApiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const accent = '#6366f1';
const accentLight = '#f1f5ff';

const initialForm = {
  cnic: '',
  phoneNumber: '',
  fullName: '',
  address: '',
  clientType: 'client',
  showroomName: '',
  additionalInfo: ''
};

const Customers = () => {
  const { createCustomer, getAllCustomers, updateCustomer, deleteCustomer, getCustomerStats } = useApiService();
  const { user: currentUser } = useAuth();

  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [itemsPerPage] = useState(10);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    loadCustomers();
    loadStats();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await getAllCustomers();
      setCustomers(response.customers || response);
      setError(null);
    } catch (err) {
      setError('Failed to load customers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const s = await getCustomerStats();
      setStats(s);
    } catch {}
  };

  const filteredCustomers = customers.filter(cust =>
    (cust.fullName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (cust.phoneNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (cust.cnic || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (cust.showroomName || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    if (!formData.phoneNumber || !formData.fullName) {
      alert('Phone Number and Full Name are required fields!');
      return;
    }
    try {
      setSubmitting(true);
      setFormError(null);
      
      const newCustomer = await createCustomer(formData);
      if (newCustomer) {
        await loadCustomers();
        setFormData(initialForm);
        setShowAddModal(false);
      }
    } catch (err) {
      const friendly =
        err?.details?.errors?.join(', ') ||
        err?.message ||
        'Failed to create customer. Please try again.';
      setFormError(friendly);
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      cnic: customer.cnic || '',
      phoneNumber: customer.phoneNumber || '',
      fullName: customer.fullName || '',
      address: customer.address || '',
      clientType: customer.clientType || 'client',
      showroomName: customer.showroomName || '',
      additionalInfo: customer.additionalInfo || ''
    });
    setShowEditModal(true);
  };

  const handleEditCustomer = async (e) => {
    e.preventDefault();
    if (!formData.phoneNumber || !formData.fullName) {
      alert('Phone Number and Full Name are required fields!');
      return;
    }
    try {
      setSubmitting(true);
      setFormError(null);
      
      await updateCustomer(editingCustomer.id, formData);
      await loadCustomers();
      setFormData(initialForm);
      setShowEditModal(false);
      setEditingCustomer(null);
    } catch (err) {
      const friendly =
        err?.details?.errors?.join(', ') ||
        err?.message ||
        'Failed to update customer. Please try again.';
      setFormError(friendly);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    
    // Check if customer has undeleted deals
    if (customer?.hasUndeletedDeals) {
      alert('Cannot delete customer. Customer is part of one or more active deals. Please delete or cancel the deals first.');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await deleteCustomer(customerId);
        await loadCustomers();
      } catch (err) {
        const errorMessage = err?.message || err?.details?.message || 'Failed to delete customer. Please try again.';
        alert(errorMessage);
      }
    }
  };

  const openViewModal = (customer) => {
    setViewingCustomer(customer);
    setShowViewModal(true);
  };

  const handleCloseModal = () => {
    setFormData(initialForm);
    setFormError(null);
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingCustomer(null);
    setViewingCustomer(null);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '4px solid #e5e7eb', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }}></div>
          <p style={{ color: '#6b7280', margin: 0 }}>Loading customers...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#dc2626', margin: '0 0 1rem' }}>{error}</p>
          <button onClick={loadCustomers} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.5rem 1rem', cursor: 'pointer' }}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: '#fafbfc', minHeight: '100vh', padding: '2rem 1.5rem', width: '100%', boxSizing: 'border-box' }}>
      {/* Header Section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '.5rem' }}>
        <div>
          <h1 className="fw-bold mb-2" style={{ color: '#1f2937', fontSize: '1.75rem', letterSpacing: '-0.025em' }}>Customers Management</h1>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>Manage and monitor your showroom customers</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold" style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem' }}>
            <Filter size={16} />
            <span className="d-none d-sm-inline">Filter</span>
          </button>
          <button className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold" style={{ background: accent, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.875rem', boxShadow: '0 1px 3px 0 rgba(99, 102, 241, 0.1), 0 1px 2px 0 rgba(99, 102, 241, 0.06)' }} onClick={() => setShowAddModal(true)}>
            <Plus size={16} />
            <span className="d-none d-sm-inline">Add Customer</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '.5rem' }}>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: accentLight, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: accent, fontSize: '1rem', fontWeight: '600' }}>{stats.totalCustomers || customers.length}</span>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Total Customers</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>All registered</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#f0fdf4', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#16a34a', fontSize: '1rem', fontWeight: '600' }}>{stats.clientCount || customers.filter(c => c.clientType === 'client').length}</span>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Clients</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Regular customers</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#fef2f2', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#dc2626', fontSize: '1rem', fontWeight: '600' }}>{stats.dealerCount || customers.filter(c => c.clientType === 'dealer').length}</span>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Dealers</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Showroom dealers</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#f0f9ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#0284c7', fontSize: '1rem', fontWeight: '600' }}>{filteredCustomers.length}</span>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Filtered Results</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Search results</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card border-0 shadow-sm mb-4" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
        <div className="card-body p-3 p-md-4">
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search customers by name, phone, CNIC, or showroom..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', paddingLeft: '16px', fontSize: '0.875rem' }}
                />
              </div>
            </div>
            <div style={{ minWidth: 180, textAlign: 'right', flex: 'none' }}>
              <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                Showing {filteredCustomers.length} of {customers.length} customers
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Customers Table */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Name</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Phone</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>CNIC</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }} className="d-none d-md-table-cell">Type</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }} className="d-none d-md-table-cell">Showroom</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }} className="d-none d-lg-table-cell">Updated By</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }} className="d-none d-lg-table-cell">Address</th>
              <th style={{ padding: '1rem', textAlign: 'right', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentCustomers.map((customer) => (
              <tr key={customer.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                  <div style={{ fontWeight: 600 }}>{customer.fullName || 'Unknown'}</div>
                </td>
                <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                  {customer.phoneNumber || 'Not provided'}
                </td>
                <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                  {customer.cnic || 'Not provided'}
                </td>
                <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }} className="d-none d-md-table-cell">
                  <span style={{ 
                    padding: '0.35rem 0.75rem', 
                    borderRadius: '999px', 
                    fontSize: '0.75rem', 
                    fontWeight: '700',
                    background: customer.clientType === 'dealer' ? '#e0f2fe' : '#eef2ff',
                    color: customer.clientType === 'dealer' ? '#075985' : '#4338ca',
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em'
                  }}>
                    {customer.clientType || 'N/A'}
                  </span>
                </td>
                <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }} className="d-none d-md-table-cell">
                  {customer.showroomName || 'N/A'}
                </td>
                <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }} className="d-none d-lg-table-cell">
                  {customer.updatedByName || customer.updatedBy || 'Not set'}
                </td>
                <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }} className="d-none d-lg-table-cell">
                  {customer.address || 'N/A'}
                </td>
                <td style={{ padding: '1rem' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button 
                      onClick={() => openViewModal(customer)}
                      style={{ background: 'none', border: 'none', padding: '0.5rem', borderRadius: '6px', cursor: 'pointer', color: accent }}
                      title="View"
                    >
                      <Eye size={16} />
                    </button>
                    <button 
                      onClick={() => openEditModal(customer)}
                      style={{ background: 'none', border: 'none', padding: '0.5rem', borderRadius: '6px', cursor: 'pointer', color: '#0284c7' }}
                      title="Edit"
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      onClick={() => handleDeleteCustomer(customer.id)}
                      disabled={customer.hasUndeletedDeals === true}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        padding: '0.5rem', 
                        borderRadius: '6px', 
                        cursor: customer.hasUndeletedDeals === true ? 'not-allowed' : 'pointer', 
                        color: customer.hasUndeletedDeals === true ? '#9ca3af' : '#dc2626',
                        opacity: customer.hasUndeletedDeals === true ? 0.5 : 1
                      }}
                      title={customer.hasUndeletedDeals === true ? 'Cannot delete: Customer is part of active deals' : 'Delete'}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
          {[...Array(totalPages)].map((_, i) => (
            <button key={i} onClick={() => setCurrentPage(i + 1)} style={{ background: currentPage === i + 1 ? accent : '#f3f4f6', color: currentPage === i + 1 ? '#fff' : '#1f2937', border: 'none', borderRadius: '6px', margin: '0 4px', padding: '0.4rem 0.9rem', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer' }}>{i + 1}</button>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '16px', padding: '2rem', minWidth: 400, maxWidth: 500, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>{showAddModal ? 'Add New Customer' : 'Edit Customer'}</h3>
              <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280', padding: '0.25rem' }}>&times;</button>
            </div>
            <form onSubmit={showAddModal ? handleAddCustomer : handleEditCustomer}>
              {formError && (
                <div style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecdd3', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.9rem' }}>
                  {formError}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>CNIC</label>
                  <input type="text" name="cnic" value={formData.cnic} onChange={handleInputChange} className="form-control" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }} maxLength={15} placeholder="Enter CNIC number (optional)" />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Phone Number*</label>
                  <input type="text" name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} className="form-control" required style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }} maxLength={15} placeholder="Enter phone number" />
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Full Name*</label>
                <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} className="form-control" required style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }} placeholder="Enter full name" />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Address</label>
                <input type="text" name="address" value={formData.address} onChange={handleInputChange} className="form-control" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }} placeholder="Enter address" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Client Type</label>
                  <select name="clientType" value={formData.clientType} onChange={handleInputChange} className="form-control" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }}>
                    <option value="client">Client</option>
                    <option value="dealer">Dealer</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Showroom Name</label>
                  <input type="text" name="showroomName" value={formData.showroomName} onChange={handleInputChange} className="form-control" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }} placeholder="Enter showroom name" />
                </div>
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Additional Info</label>
                <textarea name="additionalInfo" value={formData.additionalInfo} onChange={handleInputChange} className="form-control" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s', resize: 'vertical' }} rows={3} placeholder="Enter any additional information" />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                <button type="button" className="btn" style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem 1.5rem', fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.2s' }} onClick={handleCloseModal}>Cancel</button>
                <button type="submit" className="btn" style={{ background: accent, color: '#fff', border: 'none', borderRadius: '8px', padding: '0.75rem 1.5rem', fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 1px 3px 0 rgba(99, 102, 241, 0.1), 0 1px 2px 0 rgba(99, 102, 241, 0.06)' }} disabled={submitting}>{submitting ? 'Saving...' : (showAddModal ? 'Add Customer' : 'Update Customer')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingCustomer && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '16px', padding: '2rem', minWidth: 400, maxWidth: 500, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>Customer Details</h3>
              <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280', padding: '0.25rem' }}>&times;</button>
            </div>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>CNIC</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCustomer.cnic || 'Not provided'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Phone Number</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCustomer.phoneNumber || 'Not provided'}</div>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Full Name</label>
                <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCustomer.fullName || 'Not provided'}</div>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Address</label>
                <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCustomer.address || 'Not provided'}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Client Type</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCustomer.clientType || 'Not provided'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Showroom Name</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCustomer.showroomName || 'Not provided'}</div>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Last Updated By</label>
                <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCustomer.updatedByName || viewingCustomer.updatedBy || 'Not set'}</div>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Additional Info</label>
                <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem', minHeight: '3rem' }}>{viewingCustomer.additionalInfo || 'Not provided'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem', marginTop: '1.5rem' }}>
              <button type="button" className="btn" style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem 1.5rem', fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.2s' }} onClick={handleCloseModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers; 